import React, { FC, useEffect } from "react";
import { connect } from "react-redux";
import _ from "lodash";
import {
    BulkActionProcessingStatus,
    getRedirectUrl,
    type IBulkActionPayload,
    BulkActionType,
} from "helpers/utils/bulk-manager";
import { CancelRounded, CheckCircleOutline, ExpandMore, Replay } from "@material-ui/icons";
import { LinesheetsIcon } from "components/icons/linesheets";
import { CircularProgress } from "material-ui";
import { List, ListItem } from "material-ui/List";
import { IconButton, ListSubheader, Collapse, makeStyles } from "@material-ui/core";
import Snackbar from "@material-ui/core/Snackbar";
import SnackbarContent from "@material-ui/core/SnackbarContent";
import { format } from "lib/globalize-util";
import { theme } from "styles/theme";
import { IconAlertCircleRed } from "components/icons/assortments/icon-alert-circle-red";
import { IconAlertCircle } from "components/icons/assortments/icon-alert-circle";
import Close from "components/icons/close";
import { DATATEST } from "constants/integration";
import { closeBulkManager } from "actions/utilities/bulk-manager-actions";
import fetch from "lib/fetch";

// Export MAX_CATALOG_SIZE for use in other modules
export const MAX_CATALOG_SIZE = 500;

interface IProps {
    processes: { [key: string]: IBulkActionPayload };
    open: boolean;
    onClose: () => void;
    scope?: any;
    type: string;
    view: "item" | "catalog" | "catalogs";
    action: BulkActionType;
    existingBulkJob: IBulkJob;
}

interface IJob {
    _id?: string;
    description?: string;
    ref?: string;
    upload_id?: string;
    complete?: boolean;
    upload_url?: string;
    error?: string;
}

interface IBulkJob {
    _id?: string;
    status?: "pending" | "in-progress" | "complete" | "cancelled";
    type?: string;
    jobs?: IJob[];
    combined_job?: IJob;
    brand_id: string;
    modified_on?: Date;
    created_on?: Date;
}

const styles = {
    snackbar: { bottom: "4.5rem", boxShadow: "5px 5px 15px 5px rgba(0, 0, 0, 0.5)", borderRadius: "4px" },
    details: {
        backgroundColor: theme.palette.white,
        borderRadius: "0 0 4px 4px",
        padding: "0 0 0 0 !important",
        maxHeight: "300px",
        overflowY: "auto" as "auto",
    },
    group: {
        display: "inline-flex",
    },
};

interface IUseStylesProps {
    expanded: boolean;
}

const useStyles = makeStyles(() => ({
    root: {
        "& .MuiSnackbarContent-root": {
            color: theme.palette.white,
            flexWrap: "nowrap",
            fontFamily: "Lato, sans-serif !important",
            fontSize: "14px",
            width: "400px",
            boxShadow: "unset",
            borderRadius: (props: IUseStylesProps) => (props.expanded ? "4px 4px 0 0" : "4px"),
            transition: "border-radius 0.3s",
        },
        "& .MuiSnackbarContent-message": {
            display: "inline-flex",
            "& .MuiSvgIcon-root": {
                display: "flex",
                "&:first-child": {
                    fill: "currentColor",
                },
            },
            "& p": {
                display: "flex",
                fontSize: "14px",
                paddingTop: "2px",
                marginLeft: "10px",
            },
        },
        "& .MuiSnackbarContent-action ": {
            "& .MuiButtonBase-root": {
                marginLeft: "10px",
                height: "20px",
                width: "20px",
            },
        },
        "& .MuiListSubheader-sticky": {
            fontFamily: "Lato, sans-serif !important",
        },
    },
    expandIcon: {
        transition: "transform 0.3s",
        transform: (props: IUseStylesProps) => (props.expanded ? "rotate(0deg)" : "rotate(180deg)"),
    },
}));

const BulkManager: FC<IProps> = ({ processes, open, type, onClose, scope, view, action, existingBulkJob }) => {
    const [currentProcesses, setCurrentProcesses] = React.useState([]);
    const [expanded, setExpanded] = React.useState(false);
    const [successes, setSuccesses] = React.useState([]);
    const [errors, setErrors] = React.useState([]);
    const [cancelled, setCancelled] = React.useState([]);
    const [statusMessage, setStatusMessage] = React.useState("");
    const [statusIcon, setStatusIcon] = React.useState(null);
    const [bulkJob, setBulkJob] = React.useState<IBulkJob>(existingBulkJob || null);
    const [confirmCancel, setConfirmCancel] = React.useState(false);
    const [isCancelling, setIsCancelling] = React.useState(false);
    const controllerRefPost = React.useRef<AbortController | null>(null);
    const controllerRefGet = React.useRef<AbortController | null>(null);
    const classes = useStyles({ expanded });
    const esSyncStatsVisible = React.useMemo(
        () =>
            view === "catalog" &&
            scope.accountType === "nuorder" &&
            (scope.brand.hasPreference("adaptive_dynamic_filters") ||
                scope.brand.hasPreference("improve_linesheets_search_performance")),
        [scope.accountType, scope.brand, view],
    );
    const viewStyle = React.useMemo(
        () => (!esSyncStatsVisible ? { right: "8rem" } : { right: "23.5rem" }),
        [esSyncStatsVisible],
    );

    useEffect(() => {
        const updatedProcesses = Object.values(processes)?.filter(
            (process) => process.brandId === scope?.brand?._id && process.type == action,
        );
        setCurrentProcesses(updatedProcesses);
    }, [processes, scope, action]);

    const errorStatus = React.useMemo(() => {
        const status =
            errors.length === 0
                ? format(
                      `${successes.length} of ${currentProcesses.length} ${type} ${action === BulkActionType.ADD ? "updated" : "downloaded"}.`,
                  )
                : format(
                      `${errors.length} of ${currentProcesses.length} ${type} didn't ${action === BulkActionType.DOWNLOAD ? "download" : "update"}.`,
                  );
        return <p>{status}</p>;
    }, [errors]); // eslint-disable-line react-hooks/exhaustive-deps

    const noAvailableExports = React.useMemo(
        () => bulkJob?.status === "complete" && _.every(bulkJob?.jobs, (job) => _.isEmpty(job?.upload_url)),
        [bulkJob],
    );

    useEffect(() => {
        const progressStatusText =
            action === BulkActionType.ADD
                ? format(`Adding products to ${currentProcesses.length} ${type}...`)
                : format(`Processing ${currentProcesses.length} ${type}...`);

        const successStatusText =
            action === BulkActionType.ADD
                ? format("Products added!")
                : action === BulkActionType.DOWNLOAD
                  ? format(`${_.capitalize(type)} downloaded!`)
                  : format(`${_.capitalize(type)} processed!`);

        const updatedSuccesses = currentProcesses.filter(
            (process) => process.status === BulkActionProcessingStatus.SUCCESS,
        );
        const updatedErrors = currentProcesses.filter((process) => process.status === BulkActionProcessingStatus.ERROR);
        const cancelledList = currentProcesses.filter(
            (process) => process.status === BulkActionProcessingStatus.CANCELLED,
        );
        setSuccesses(updatedSuccesses);
        setErrors(updatedErrors);
        setCancelled(cancelledList);

        const excluded = currentProcesses.filter((process) => process?.data?.exclude);

        if (bulkJob?.status === "complete" && noAvailableExports) {
            setStatusMessage(format("No matching products to export"));
            setStatusIcon(<IconAlertCircle style={{ color: theme.palette.white }} />);
            return;
        }
        if (cancelledList.length > 0) {
            setStatusMessage(format("Job Cancelled!"));
            setStatusIcon(<IconAlertCircle style={{ color: theme.palette.white }} />);
        } else if (updatedErrors.length > 0 && excluded.length === 0) {
            setStatusMessage(format("Something went wrong."));
            setStatusIcon(
                !updatedSuccesses.length ? <IconAlertCircleRed /> : <IconAlertCircle style={{ color: "#fae27f" }} />,
            );
        } else if (
            updatedSuccesses.length + excluded.length >= currentProcesses.length &&
            (!bulkJob || bulkJob.status === "complete") &&
            excluded.length != currentProcesses.length
        ) {
            setStatusMessage(successStatusText);
            setStatusIcon(<CheckCircleOutline style={{ color: "#9fc4a9", fontSize: "2rem" }} />);
        } else {
            setStatusMessage(progressStatusText);
            setStatusIcon(<CircularProgress size={20} />);
        }
    }, [action, currentProcesses, type, bulkJob, noAvailableExports]);

    const handleClose = () => {
        if (bulkJob && bulkJob._id && action === BulkActionType.DOWNLOAD) {
            fetch(`/api/v3.1/brand/${scope?.brand?._id}/bulk-catalog/close/${bulkJob._id}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
            });
        }
        onClose();
    };

    const handleExpand = () => {
        setExpanded(!expanded);
    };

    const getStatusIcon = (process: IBulkActionPayload) => {
        const isInfo = process.data?.exclude && process.error;
        if (isInfo) {
            return <IconAlertCircle style={{ color: "#fae27f" }} />;
        } else if (process.status === BulkActionProcessingStatus.SUCCESS) {
            return <CheckCircleOutline style={{ color: "#9fc4a9" }} />;
        } else if (process.status === BulkActionProcessingStatus.ERROR && !isInfo) {
            return <IconAlertCircleRed />;
        } else if (process.status === BulkActionProcessingStatus.CANCELLED) {
            return <CancelRounded style={{ color: "#808080" }} />;
        } else {
            return <CircularProgress size={20} />;
        }
    };

    // Polling logic for bulkJob status (no overlapping fetches, fixed dependencies)
    useEffect(() => {
        if (!bulkJob || !bulkJob._id || ["complete", "cancelled"].includes(bulkJob.status)) {
            return;
        }
        let cancelled = false;
        const controller = new AbortController();
        controllerRefGet.current = controller;
        let timeoutId: NodeJS.Timeout;
        async function poll() {
            if (cancelled || (isCancelling && !confirmCancel)) {
                return;
            }
            try {
                const res = await fetch(`/api/v3.1/brand/${scope?.brand?._id}/bulk-catalog/export/${bulkJob._id}`, {
                    method: "GET",
                    headers: { "Active-Brand": scope?.brand?._id },
                    signal: controller.signal,
                });
                const data = await res.json();
                if (!cancelled) {
                    if (data.jobs && Array.isArray(data.jobs)) {
                        setCurrentProcesses((prevProcesses) =>
                            prevProcesses.map((process) => {
                                const job = data.jobs.find((j) => j.ref === process.id);
                                if (job && job.complete) {
                                    return {
                                        ...process,
                                        status:
                                            process.data?.catalog_size > MAX_CATALOG_SIZE
                                                ? BulkActionProcessingStatus.WARNING
                                                : job.error || !job.upload_url
                                                  ? BulkActionProcessingStatus.ERROR
                                                  : BulkActionProcessingStatus.SUCCESS,
                                        error:
                                            process.data?.catalog_size > MAX_CATALOG_SIZE
                                                ? format("(click to process download)")
                                                : !job.upload_url
                                                  ? format("(no product matches)")
                                                  : job?.error,
                                        data: {
                                            ...process.data,
                                            complete: job.complete,
                                            upload_url: job.upload_url,
                                            exclude:
                                                !job.upload_url ||
                                                process.data?.catalog_size > MAX_CATALOG_SIZE ||
                                                job.error,
                                        },
                                    };
                                }
                                return process;
                            }),
                        );
                    }
                    setBulkJob((prev) => {
                        // Only update if status actually changed
                        if (prev && prev.status !== data.status) {
                            return data;
                        }
                        return prev;
                    });
                    if (data.status !== "complete") {
                        timeoutId = setTimeout(poll, 2000);
                    }
                }
            } catch (e) {
                if (!cancelled || e.name != "AbortError") {
                    timeoutId = setTimeout(poll, 2000);
                }
            }
        }
        poll();
        return () => {
            cancelled = true;
            controller.abort();
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [bulkJob, scope?.brand?._id, isCancelling, confirmCancel]);

    // Download zip file when bulkJob is complete
    useEffect(() => {
        if (!bulkJob || bulkJob.status !== "complete" || noAvailableExports) {
            return;
        }

        const exclude = currentProcesses
            .filter((process) => process?.data?.exclude || process.error)
            ?.map((process) => process.id);

        if (exclude.length === currentProcesses.length) {
            return;
        }

        const downloadZip = async () => {
            try {
                const res = await fetch(`/api/v3.1/brand/${scope?.brand?._id}/bulk-catalog/download/${bulkJob._id}`, {
                    method: "POST",
                    headers: { "Active-Brand": scope?.brand?._id },
                });
                if (!res.ok) {
                    return;
                }
                const blob = await res.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `bulk-catalog-${bulkJob._id}.zip`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            } catch (e) {
                // Optionally handle error
            }
        };
        downloadZip();
    }, [bulkJob, noAvailableExports]); // eslint-disable-line react-hooks/exhaustive-deps

    // Retry handler for failed downloads
    const handleRetry = React.useCallback(
        async (process) => {
            try {
                const fetchOptions = {
                    method: process.method,
                    headers: {
                        "Active-Brand": scope?.brand?._id,
                    },
                    ...(process.data.body ? { body: JSON.stringify(process.data.body) } : {}),
                };
                const res = await fetch(process.route, fetchOptions);
                if (process?.data?.isImmediateDownload) {
                    const blob = await res.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `${process.name}.${process.data.fileExtension}`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                    setCurrentProcesses((prevProcesses) =>
                        prevProcesses.map((p) => {
                            if (p.id === process.id) {
                                return {
                                    ...p,
                                    status: BulkActionProcessingStatus.SUCCESS,
                                    data: { ...p.data, upload_url: url, exclude: false },
                                    error: null,
                                };
                            }
                            return p;
                        }),
                    );
                    return;
                } else {
                    const data = await res.json();
                    const retriedExport = data?.jobs?.[0];
                    if (retriedExport.upload_url || retriedExport.id) {
                        setCurrentProcesses((prevProcesses) =>
                            prevProcesses.map((p) => {
                                if (p.id === process.id) {
                                    return {
                                        ...p,
                                        status: BulkActionProcessingStatus.SUCCESS,
                                        data: { ...p.data, ...retriedExport },
                                        error: format("(click here to view export)"),
                                    };
                                }
                                return p;
                            }),
                        );
                    } else {
                        setCurrentProcesses((prevProcesses) =>
                            prevProcesses.map((p) => {
                                if (p.id === process.id) {
                                    return {
                                        ...p,
                                        status: BulkActionProcessingStatus.ERROR,
                                        error: data.error || format("Failed to export"),
                                    };
                                }
                                return p;
                            }),
                        );
                    }
                }
            } catch (e) {
                setCurrentProcesses((prevProcesses) =>
                    prevProcesses.map((p) => {
                        if (p.id === process.id) {
                            return {
                                ...p,
                                status: BulkActionProcessingStatus.ERROR,
                                error: format("Failed to export"),
                            };
                        }
                        return p;
                    }),
                );
            }
        },
        [scope, setCurrentProcesses],
    );

    const handleRedirect = React.useCallback(
        (process: IBulkActionPayload) => {
            if (process.status === BulkActionProcessingStatus.SUCCESS) {
                const redirectUrl = getRedirectUrl(process.data);
                const linesheetUrl = `/brand/${scope?.brand?._id}/${scope?.brand?.hasPreference("linesheets_are_catalogs") ? "catalog" : "linesheet"}/${process.id}`;
                const shouldViewExport =
                    process?.data?.catalog_size > MAX_CATALOG_SIZE &&
                    process?.error == format("(click here to view export)");
                if (shouldViewExport) {
                    setCurrentProcesses((prevProcesses) =>
                        prevProcesses.map((p) => {
                            if (p.id === process.id) {
                                return {
                                    ...p,
                                    data: { ...p.data, exclude: false },
                                    error: null,
                                };
                            }
                            return p;
                        }),
                    );
                    window.open(redirectUrl, "_blank");
                } else {
                    window.open(linesheetUrl, "_blank");
                }
            } else if (!process.data.upload_url || !_.isEmpty(process.error)) {
                setCurrentProcesses((prevProcesses) =>
                    prevProcesses.map((p) => {
                        if (p.id === process.id) {
                            return {
                                ...p,
                                status: BulkActionProcessingStatus.PROCESSING,
                                error: null,
                            };
                        }
                        return p;
                    }),
                );
                handleRetry(process);
            } else {
                const redirectUrl = getRedirectUrl(process.data);
                window.open(redirectUrl, "_blank");
            }
        },
        [scope, setCurrentProcesses, handleRetry],
    );

    useEffect(() => {
        const largeProcesses = currentProcesses.filter(
            (process) =>
                process?.data?.exclude &&
                process?.data?.catalog_size > MAX_CATALOG_SIZE &&
                process?.status === BulkActionProcessingStatus.READY,
        );
        if (action == BulkActionType.DOWNLOAD && largeProcesses.length > 0) {
            (async () => {
                for (const process of largeProcesses) {
                    await handleRedirect(process);
                }
            })();
        }
    }, [currentProcesses, action, handleRedirect]);

    const handleCancelClick = (e) => {
        e.preventDefault();
        if (confirmCancel) {
            handleCancel();
        } else {
            setConfirmCancel(true);
        }
    };

    const handleCancel = async () => {
        try {
            setIsCancelling(true);
            setConfirmCancel(false);
            if (controllerRefPost.current) {
                controllerRefPost.current.abort();
            }
            if (controllerRefGet.current) {
                controllerRefGet.current.abort();
            }

            if (bulkJob && bulkJob._id) {
                await fetch(`/api/v3.1/brand/${scope?.brand?._id}/bulk-catalog/cancel/${bulkJob._id}`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                });
            }

            setBulkJob((prev) => ({
                ...prev,
                status: "cancelled",
            }));
            setCurrentProcesses((prev) =>
                prev.map((process) => ({
                    ...process,
                    status: BulkActionProcessingStatus.CANCELLED,
                    error: null,
                })),
            );
        } catch (error) {
            // TODO: Handle error appropriately
        } finally {
            setIsCancelling(false);
        }
    };

    const showCloseButton = React.useMemo(() => {
        return (
            currentProcesses.length === successes.length + errors.length + cancelled.length ||
            ["complete", "cancelled"].includes(bulkJob?.status)
        );
    }, [currentProcesses, successes, errors, cancelled, bulkJob]);

    const showCancelLink = React.useMemo(() => {
        const cond1 =
            currentProcesses.length > 0 &&
            currentProcesses.length !== successes.length + errors.length + cancelled.length;
        const cond2 =
            !bulkJob || (bulkJob && bulkJob._id && bulkJob?.status !== "complete" && bulkJob?.status !== "cancelled");
        return action === BulkActionType.DOWNLOAD && cond1 && cond2;
    }, [action, currentProcesses, successes, errors, cancelled, bulkJob]);

    return (
        <div className={classes.root}>
            <Snackbar
                open={open}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                style={{ ...styles.snackbar, right: viewStyle.right }}
            >
                <div data-test={DATATEST.BULK_MANAGER.TOAST_DRAWER}>
                    <SnackbarContent
                        message={
                            <div data-test={DATATEST.BULK_MANAGER.HEADER} style={styles.group}>
                                {statusIcon}
                                <p>{statusMessage}</p>
                            </div>
                        }
                        action={
                            <div>
                                <IconButton
                                    size="small"
                                    onClick={handleExpand}
                                    className={classes.expandIcon}
                                    data-test={DATATEST.BULK_MANAGER.EXPAND}
                                >
                                    <ExpandMore htmlColor={theme.palette.white} style={{ fontSize: "2.5rem" }} />
                                </IconButton>
                                {showCloseButton && (
                                    <IconButton
                                        size="small"
                                        onClick={handleClose}
                                        data-test={DATATEST.BULK_MANAGER.DISMISS}
                                    >
                                        <Close style={{ color: theme.palette.white }} />
                                    </IconButton>
                                )}
                            </div>
                        }
                    />
                    <Collapse
                        in={expanded}
                        timeout="auto"
                        unmountOnExit
                        style={{ maxWidth: "400px", overflowWrap: "break-word" }}
                    >
                        <List style={styles.details} data-test={DATATEST.BULK_MANAGER.PROCESSES}>
                            <ListSubheader
                                data-test={DATATEST.BULK_MANAGER.INFO}
                                style={{
                                    backgroundColor:
                                        errors.length + successes.length !== currentProcesses.length
                                            ? "#F0F2F2"
                                            : successes.length === currentProcesses.length
                                              ? "#EFF5F1"
                                              : errors.length === currentProcesses.length
                                                ? "#FBF0EF"
                                                : "#FEFBED",
                                    color: theme.palette.darkGrey,
                                    fontSize: "12px",
                                }}
                            >
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                    {errorStatus}
                                    {showCancelLink && (
                                        <a
                                            href="#"
                                            onClick={handleCancelClick}
                                            style={{
                                                color: theme.palette.accent1Color,
                                                textDecoration: "underline",
                                                cursor: "pointer",
                                                marginLeft: "10px",
                                            }}
                                            data-test={DATATEST.BULK_MANAGER.CANCEL}
                                        >
                                            {confirmCancel ? format("Confirm cancel all?") : format("Cancel all")}
                                        </a>
                                    )}
                                </div>
                            </ListSubheader>
                            {_.sortBy(currentProcesses, (process) => !process.data?.exclude).map((process) => {
                                const canRetry =
                                    bulkJob &&
                                    action === BulkActionType.DOWNLOAD &&
                                    process.status === BulkActionProcessingStatus.ERROR &&
                                    process.data?.upload_url;
                                return (
                                    <ListItem
                                        key={process.id}
                                        leftIcon={
                                            canRetry ? (
                                                <Replay
                                                    style={{ color: theme.palette.darkGrey, cursor: "pointer" }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleRetry(process);
                                                    }}
                                                    titleAccess="Retry download"
                                                />
                                            ) : (
                                                <LinesheetsIcon style={{ color: theme.palette.darkGrey }} />
                                            )
                                        }
                                        rightIcon={getStatusIcon(process)}
                                        onClick={() => handleRedirect(process)}
                                    >
                                        <p>{process.name}</p>
                                        {process?.data?.exclude && process?.error && !noAvailableExports && (
                                            <p style={{ fontSize: "12px" }}>{process.error}</p>
                                        )}
                                    </ListItem>
                                );
                            })}
                        </List>
                    </Collapse>
                </div>
            </Snackbar>
        </div>
    );
};

const mapStateToProps = (state, ownProps) => ({
    scope: state.scope,
    processes: state.bulkManager.processes,
    open: state.bulkManager.open,
    type: state.bulkManager.type,
    view: ownProps.view,
    action: ownProps.action,
    existingBulkJob: state.bulkManager.bulkJob,
});

const mapDispatchToProps = (dispatch) => ({
    onClose: () => dispatch(closeBulkManager()),
});

export default connect(mapStateToProps, mapDispatchToProps)(BulkManager);
